import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { Variables } from "../../Variations/Variations";
import { toast } from "react-toastify";
import { AiOutlineRight } from "react-icons/ai";

const ManageVideos = () => {
  const { VideoID } = useParams();
  const navigate = useNavigate();
  const videoIdNum = Number(VideoID) || 0;

  // ---- State (matches API JSON) ----
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [widgetId, setWidgetId] = useState(null); // number|null
  const [isPublished, setIsPublished] = useState(false);
  const [titleAr, setTitleAr] = useState("");
  const [bodyAr, setBodyAr] = useState("");

  // ---- Widgets for select ----
  const [widgets, setWidgets] = useState([]);
  // Build options from API data
  const widgetOptions = (Array.isArray(widgets) ? widgets : [])
    .filter((w) => parseInt(w.WidgetTypeId) === 4)
    .map((w) => ({
      value: w.Id,
      label: w.DisplayName ?? w.Name ?? String(w.Id),
    }));

  // Selected option from ID
  const selectedWidgetOption =
    widgetOptions.find((o) => o.value === widgetId) || null;

  // put these above handleSubmit (anywhere in the component file)
  const isBlank = (v) => typeof v !== "string" || v.trim() === "";

  // ---- Loaders ----
  async function loadWidgets() {
    try {
      const res = await fetch(`${Variables.API_URL}Rasha/Widget/Get`);
      if (res.ok) setWidgets(await res.json());
      else toast.error("Failed to load widgets");
    } catch {
      toast.error("Failed to load widgets");
    }
  }

  async function loadVideo() {
    try {
      const res = await fetch(
        `${Variables.API_URL}Rasha/Video/Get/${videoIdNum}`
      );
      if (!res.ok) return;
      const data = await res.json();
      console.log(data);
     setTitle(data.Video.title ?? data.Video.Title ?? "");
      setBody(data.Video.body ?? data.Video.Body ?? "");
      setVideoUrl(data.Video.videoUrl ?? data.Video.VideoUrl ?? "");
      setWidgetId(data.Video.widgetId ?? data.Video.WidgetId ?? null);
      setIsPublished(
        typeof data.Video.isPublished === "boolean"
          ? data.Video.isPublished
          : Boolean(data.Video.IsPublished)
      );
      setTitleAr(data.Video.titleAr ?? data.Video.TitleAr ?? "");
      setBodyAr(data.Video.bodyAr ?? data.Video.BodyAr ?? "");
    } catch {
      toast.error("Failed to load Video");
    }
  }

  useEffect(() => {
    loadWidgets();
    if (videoIdNum !== 0) loadVideo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoIdNum]);



  // ---- Validation ----
  function isValid() {
    if (isBlank(title)) {
      toast.error("Title is required");
      return false;
    }
    if (isBlank(body)) {
      toast.error("Body is required");
      return false;
    }
    if (!widgetId || Number(widgetId) <= 0) {
      toast.error("Widget is required");
      return false;
    }
   

    return true;
  }
  // ---- Save (Add / Update) ----
  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid()) return;

    try {
      let payload;
      if (videoIdNum === 0) {
        payload = {
          title,
          body,
          videoUrl,
          widgetId, // number
          isPublished,
          titleAr,
          bodyAr,
        };
      } else {
        payload = {
          id: videoIdNum,
          title,
          body,
          videoUrl,
          widgetId, // number
          isPublished,
          titleAr,
          bodyAr,
        };
      }
      console.log(payload)

      const res = await fetch(`${Variables.API_URL}Rasha/Video/Upsert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(await res.text());

      toast.success(
        videoIdNum === 0
          ? "Video added successfully"
          : "Video updated successfully"
      );
      navigate("/admin/rasha/Video");
    } catch (err) {
      toast.error(err.message || "Failed to save");
    }
  }

  // ---- Render ----
  return (
    <div className="rightSide">
      <div className="container-fluid mt-3">
        {/* Header */}
        <div className="row mb-3">
          <div className="col-12">
            <h2>
              {videoIdNum === 0 ? "Add" : `Manage ${title || "Video"}`}{" "}
              Video
            </h2>
            <ul className="portalPath">
              <li>
                Home <AiOutlineRight />
              </li>
              <li>
                <Link to="/admin/rasha/Video">
                  Video <AiOutlineRight />
                </Link>
              </li>
              <li>{videoIdNum === 0 ? "Add" : "Manage"} Video</li>
            </ul>
          </div>
        </div>

        {/* Form */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-end mb-3">
                  <NavLink
                    to="/admin/rasha/Video"
                    className="btn btn-secondary me-2"
                  >
                    Cancel
                  </NavLink>
                  <button className="btn btn-primary" onClick={handleSubmit}>
                    Save
                  </button>
                </div>

                <div className="row gy-3">
                  {/* Title / TitleAr */}
                  <div className="col-lg-6">
                    <label className="mb-2">
                      Title <span className="required">*</span>
                    </label>
                    <input
                      id="title"
                      className="form-control"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Title"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="mb-2">Title (Arabic)</label>
                    <input
                      id="titleAr"
                      className="form-control"
                      value={titleAr}
                      onChange={(e) => setTitleAr(e.target.value)}
                      placeholder="Title in Arabic"
                    />
                  </div>

                  {/* Body / BodyAr */}
                  <div className="col-lg-6">
                    <label className="mb-2">Body</label>
                    <textarea
                      id="body"
                      className="form-control"
                      rows={5}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Content body"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="mb-2">Body (Arabic)</label>
                    <textarea
                      id="bodyAr"
                      className="form-control"
                      rows={5}
                      value={bodyAr}
                      onChange={(e) => setBodyAr(e.target.value)}
                      placeholder="Content body in Arabic"
                    />
                  </div>


                  <div className="col-lg-12">
                    <label className="mb-2">Video URL</label>
                    <input
                      id="VideoUrl"
                      className="form-control"
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e.target.value)}
                      placeholder="Video Url"
                    />
                  </div>

                  {/* Publish toggle */}
                  <div className="form-group col-lg-3 published-check d-flex align-items-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="publish"
                      checked={isPublished}
                      onChange={(e) => setIsPublished(e.target.checked)}
                    />
                    &nbsp;
                    <label className="form-check-label" htmlFor="publish">
                      Publish
                    </label>
                  </div>

                  {/* Widget select */}
                  <div className="col-lg-6">
                    <label className="mb-2">
                      Widget <span className="required">*</span>
                    </label>
                    <Select
                      options={widgetOptions}
                      value={selectedWidgetOption}
                      onChange={(opt) => setWidgetId(opt ? opt.value : null)}
                      placeholder="Select a widget…"
                      isClearable
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageVideos;
